/* Card de invitación a eventos para la pantalla de Inicio.
 *
 * - Muestra las invitaciones del usuario (endpoint /eventos/mis-invitaciones).
 * - PENDIENTE: card de invitación con CTA "Ver y confirmar".
 * - CONFIRMADO: card de recordatorio con fecha/hora/lugar.
 * - Al tocar, abre un modal con toda la info; si está pendiente permite
 *   confirmar/rechazar y elegir cuántos acompañantes llevará (hasta el máximo).
 * - Eventos con pedido (requiere_pedido): el modal muestra el catálogo de ítems
 *   con precio; el invitado arma su pedido, ve el total y acepta el descuento
 *   de sueldo. El pedido se puede editar hasta el fin de confirmación.
 * - Eventos con QR (requiere_qr): confirmado el pedido/asistencia, el modal
 *   muestra el QR dinámico para el retiro (rota como en Entregas Masivas).
 */
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import Eventos, { EventoItem, Invitacion, PedidoLinea } from "src/services/eventos";

const fmtFecha = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(String(iso).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-PY", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtMonto = (n: number | null | undefined) =>
  `Gs. ${Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 0 })}`;

interface PedidoState {
  items: EventoItem[];
  pedido: PedidoLinea[];
  total: number;
  consentimiento_fecha: string | null;
  /** true = el pedido ya fue confirmado (es único, no se puede modificar). */
  pedido_confirmado: boolean;
  permite_cuotas: boolean;
  max_cuotas: number | null;
  cuotas: number | null;
}

const EventosInvitacionCard: React.FC = () => {
  const [present] = useIonToast();
  const [invitaciones, setInvitaciones] = useState<Invitacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [seleccion, setSeleccion] = useState<Invitacion | null>(null);
  const [acomp, setAcomp] = useState(0);
  const [guardando, setGuardando] = useState(false);

  // Pedido (eventos con requiere_pedido)
  const [pedidoInfo, setPedidoInfo] = useState<PedidoState | null>(null);
  const [cantidades, setCantidades] = useState<Record<number, number>>({});
  const [aceptaDescuento, setAceptaDescuento] = useState(false);
  const [cuotasSel, setCuotasSel] = useState(1);
  const [cargandoPedido, setCargandoPedido] = useState(false);

  // QR de retiro (eventos con requiere_qr)
  const [qrInfo, setQrInfo] = useState<Awaited<ReturnType<typeof Eventos.miQR>> | null>(null);
  const [qrSeconds, setQrSeconds] = useState(0);
  const tickRef = useRef<number | null>(null);
  const refreshRef = useRef<number | null>(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const data = await Eventos.misInvitaciones();
      setInvitaciones(data);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (refreshRef.current) window.clearTimeout(refreshRef.current);
    };
  }, []);

  const abrir = async (inv: Invitacion) => {
    setSeleccion(inv);
    setAcomp(inv.acompanantes_confirmados ?? 0);
    setPedidoInfo(null);
    setCantidades({});
    setAceptaDescuento(false);
    setCuotasSel(1);
    setQrInfo(null);

    if (inv.requiere_pedido) {
      setCargandoPedido(true);
      try {
        const info = await Eventos.obtenerPedido(inv.evento_id);
        setPedidoInfo(info);
        const cant: Record<number, number> = {};
        (info.pedido || []).forEach((l) => (cant[l.item_id] = l.cantidad));
        setCantidades(cant);
        setAceptaDescuento(Boolean(info.consentimiento_fecha));
        setCuotasSel(info.cuotas ?? 1);
      } catch {
        setPedidoInfo(null);
      } finally {
        setCargandoPedido(false);
      }
    }

    if (inv.requiere_qr && inv.estado_confirmacion === "CONFIRMADO") {
      cargarQR(inv.evento_id);
    }
  };

  const cerrar = () => {
    setSeleccion(null);
    setQrInfo(null);
    if (tickRef.current) window.clearInterval(tickRef.current);
    if (refreshRef.current) window.clearTimeout(refreshRef.current);
  };

  const cargarQR = async (eventoId: number) => {
    try {
      const data = await Eventos.miQR(eventoId);
      setQrInfo(data);
      if (!data.ya_retirado && data.ttl_seconds) setQrSeconds(data.ttl_seconds);
    } catch {
      setQrInfo(null);
    }
  };

  // Countdown y refresco automático del QR al expirar la ventana.
  useEffect(() => {
    if (!seleccion || !qrInfo || qrInfo.ya_retirado || !qrInfo.qr) return;
    if (tickRef.current) window.clearInterval(tickRef.current);
    if (refreshRef.current) window.clearTimeout(refreshRef.current);

    tickRef.current = window.setInterval(() => {
      setQrSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    const ms = Math.max((qrInfo.ttl_seconds || 60) - 1, 1) * 1000;
    refreshRef.current = window.setTimeout(() => {
      if (seleccion) cargarQR(seleccion.evento_id);
    }, ms);

    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (refreshRef.current) window.clearTimeout(refreshRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrInfo?.qr, qrInfo?.ttl_seconds, qrInfo?.ya_retirado]);

  const setCantidad = (item: EventoItem, delta: number) => {
    setCantidades((prev) => {
      const actual = prev[item.id!] ?? 0;
      let nuevo = actual + delta;
      if (nuevo < 0) nuevo = 0;
      if (item.disponibles !== null && item.disponibles !== undefined && nuevo > item.disponibles) {
        nuevo = item.disponibles;
      }
      return { ...prev, [item.id!]: nuevo };
    });
  };

  const totalUnidades = Object.values(cantidades).reduce((a, b) => a + b, 0);
  const totalPedido = (pedidoInfo?.items || []).reduce(
    (acc, it) => acc + (cantidades[it.id!] ?? 0) * Number(it.precio || 0),
    0
  );

  const confirmar = async (asistira: boolean) => {
    if (!seleccion) return;
    const conPedido = Boolean(seleccion.requiere_pedido) && asistira && totalUnidades > 0;
    if (conPedido && !aceptaDescuento) {
      present({
        message: "Debés aceptar el descuento de sueldo para confirmar el pedido",
        duration: 3500,
        color: "warning",
      });
      return;
    }
    setGuardando(true);
    try {
      if (conPedido) {
        const items = Object.entries(cantidades)
          .filter(([, cant]) => cant > 0)
          .map(([itemId, cant]) => ({ item_id: Number(itemId), cantidad: cant }));
        await Eventos.guardarPedido(seleccion.evento_id, items, true, acomp, cuotasSel);
        present({
          message: `¡Pedido confirmado! Total: ${fmtMonto(totalPedido)}${cuotasSel > 1 ? ` en ${cuotasSel} cuotas` : ""}`,
          duration: 3500,
          color: "success",
        });
      } else {
        await Eventos.confirmar(seleccion.evento_id, asistira, asistira ? acomp : 0);
        present({
          message: asistira ? "¡Asistencia confirmada!" : "Registrado: no asistirás",
          duration: 3000,
          color: "success",
        });
      }
      cerrar();
      await cargar();
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo confirmar",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return null;
  if (!invitaciones.length) return null;

  const puedeEditarPedido = (inv: Invitacion) => {
    const fin = new Date(String(inv.fecha_fin_confirmacion).replace(" ", "T"));
    return !isNaN(fin.getTime()) && fin.getTime() > Date.now();
  };

  return (
    <div className="container mt-1">
      {invitaciones.map((inv) => {
        const confirmado = inv.estado_confirmacion === "CONFIRMADO";
        const rechazado = inv.estado_confirmacion === "RECHAZADO";
        const abierto = inv.requiere_invitacion !== undefined && !inv.requiere_invitacion;
        const colorBorde = confirmado ? "#2e7d32" : rechazado ? "#9e9e9e" : "#1565c0";
        return (
          <div
            key={inv.evento_id}
            onClick={() => abrir(inv)}
            style={{
              cursor: "pointer",
              background: "#fff",
              borderRadius: 10,
              borderLeft: `6px solid ${colorBorde}`,
              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              padding: "12px 14px",
              marginBottom: 10,
            }}
          >
            <Typography variant="caption" style={{ color: colorBorde, fontWeight: 700 }}>
              {confirmado
                ? "✓ ASISTENCIA CONFIRMADA"
                : rechazado
                  ? "NO ASISTIRÁS"
                  : abierto
                    ? (inv.requiere_pedido ? "🍽 PEDIDOS ABIERTOS" : "📣 EVENTO ABIERTO")
                    : inv.requiere_pedido
                      ? "🍽 TENÉS UNA INVITACIÓN — PEDIDOS ABIERTOS"
                      : "🎉 TENÉS UNA INVITACIÓN"}
            </Typography>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {inv.tema}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {fmtFecha(inv.fecha_inicio_evento)}
            </Typography>
            {inv.lugar && (
              <Typography variant="body2" color="textSecondary">
                📍 {inv.lugar}
              </Typography>
            )}
            {confirmado && Boolean(inv.requiere_qr) && (
              <Typography variant="caption" style={{ color: "#2e7d32", fontWeight: 600 }}>
                Tocá para ver tu QR de retiro →
              </Typography>
            )}
            {!confirmado && !rechazado && (
              <Typography variant="caption" style={{ color: "#1565c0", fontWeight: 600 }}>
                {inv.requiere_pedido ? "Tocá para armar tu pedido →" : "Tocá para ver y confirmar →"}
              </Typography>
            )}
          </div>
        );
      })}

      {/* Modal de detalle / confirmación */}
      <IonModal isOpen={!!seleccion} onDidDismiss={cerrar}>
        <IonHeader>
          <IonToolbar>
            <IonTitle style={{ fontSize: 16 }}>Invitación</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={cerrar}>Cerrar</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {guardando ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <IonSpinner />
            </div>
          ) : seleccion ? (
            <div style={{ padding: 16 }}>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                {seleccion.tema}
              </Typography>
              {seleccion.descripcion && (
                // pre-wrap: respeta los saltos de línea y espacios tal como el
                // admin escribió la descripción en el textarea de creación/edición.
                <Typography variant="body2" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                  {seleccion.descripcion}
                </Typography>
              )}
              <div style={{ marginTop: 12 }}>
                <Typography variant="body2"><strong>Inicio:</strong> {fmtFecha(seleccion.fecha_inicio_evento)}</Typography>
                <Typography variant="body2"><strong>Fin:</strong> {fmtFecha(seleccion.fecha_fin_evento)}</Typography>
                {seleccion.lugar && (
                  <Typography variant="body2"><strong>Lugar:</strong> {seleccion.lugar}</Typography>
                )}
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                  Confirmá hasta el {fmtFecha(seleccion.fecha_fin_confirmacion)}
                </Typography>
              </div>

              {/* Catálogo / pedido (eventos con requiere_pedido) */}
              {Boolean(seleccion.requiere_pedido) && (
                <div style={{ marginTop: 14 }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    {pedidoInfo?.pedido_confirmado
                      ? puedeEditarPedido(seleccion) ? "Editá tu pedido" : "Tu pedido"
                      : "Armá tu pedido"}
                  </Typography>
                  {!cargandoPedido && pedidoInfo && puedeEditarPedido(seleccion) && (
                    <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                      Podés editar tu pedido hasta el {fmtFecha(seleccion.fecha_fin_confirmacion)}.
                    </Typography>
                  )}
                  {cargandoPedido && (
                    <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
                      <IonSpinner />
                    </div>
                  )}
                  {!cargandoPedido && pedidoInfo && (
                    <>
                      {pedidoInfo.items.map((it) => {
                        const cant = cantidades[it.id!] ?? 0;
                        // Editable hasta fecha_fin_confirmacion (el backend además
                        // lo cierra si el retiro ya fue registrado).
                        const editable =
                          seleccion.estado_confirmacion !== "RECHAZADO" &&
                          puedeEditarPedido(seleccion);
                        const agotado = it.disponibles !== null && it.disponibles !== undefined && it.disponibles <= 0 && cant === 0;
                        return (
                          <div
                            key={it.id}
                            style={{
                              border: "1px solid #eee",
                              borderRadius: 8,
                              padding: "8px 10px",
                              marginTop: 8,
                              opacity: agotado ? 0.55 : 1,
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <Typography variant="body2" style={{ fontWeight: 700 }}>{it.nombre}</Typography>
                                {it.descripcion && (
                                  <Typography variant="caption" color="textSecondary">{it.descripcion}</Typography>
                                )}
                                <Typography variant="body2">{fmtMonto(it.precio)}</Typography>
                                {agotado && (
                                  <Typography variant="caption" style={{ color: "#c62828", fontWeight: 700 }}>
                                    AGOTADO
                                  </Typography>
                                )}
                              </div>
                              {editable ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <IonButton size="small" fill="outline" disabled={cant === 0} onClick={() => setCantidad(it, -1)}>−</IonButton>
                                  <span style={{ minWidth: 20, textAlign: "center", fontWeight: 700 }}>{cant}</span>
                                  <IonButton size="small" fill="outline" disabled={agotado || (it.disponibles !== null && it.disponibles !== undefined && cant >= it.disponibles)} onClick={() => setCantidad(it, 1)}>+</IonButton>
                                </div>
                              ) : (
                                cant > 0 && <span style={{ fontWeight: 700 }}>x{cant}</span>
                              )}
                            </div>
                            {cant > 0 && (
                              <Typography variant="caption" color="textSecondary">
                                Subtotal: {fmtMonto(cant * Number(it.precio || 0))}
                              </Typography>
                            )}
                          </div>
                        );
                      })}
                      <div
                        style={{
                          marginTop: 10,
                          background: "#f5f5f5",
                          borderRadius: 8,
                          padding: 10,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" style={{ fontWeight: 700 }}>Total</Typography>
                        <Typography variant="body2" style={{ fontWeight: 700 }}>{fmtMonto(totalPedido)}</Typography>
                      </div>

                      {/* Cuotas: el invitado elige en cuántas se le descuenta lo retirado */}
                      {pedidoInfo.permite_cuotas && pedidoInfo.pedido_confirmado && !puedeEditarPedido(seleccion) && (pedidoInfo.cuotas ?? 1) > 1 && (
                        <Typography variant="body2" style={{ marginTop: 6 }}>
                          Descuento en <strong>{pedidoInfo.cuotas} cuotas</strong> de aprox.{" "}
                          {fmtMonto(Math.round((pedidoInfo.total || 0) / (pedidoInfo.cuotas || 1)))} c/u.
                        </Typography>
                      )}
                      {pedidoInfo.permite_cuotas && totalUnidades > 0 && puedeEditarPedido(seleccion) && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 14 }}>Descontar en:</span>
                          <select
                            value={cuotasSel}
                            onChange={(e) => setCuotasSel(Number(e.target.value))}
                            style={{ fontSize: 15, padding: "6px 10px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                          >
                            {Array.from({ length: pedidoInfo.max_cuotas ?? 1 }, (_, i) => i + 1).map((n) => (
                              <option key={n} value={n}>{n === 1 ? "1 cuota" : `${n} cuotas`}</option>
                            ))}
                          </select>
                          {cuotasSel > 1 && (
                            <span style={{ fontSize: 13, color: "#666" }}>
                              aprox. {fmtMonto(Math.round(totalPedido / cuotasSel))} por cuota
                            </span>
                          )}
                        </div>
                      )}

                      {totalUnidades > 0 && puedeEditarPedido(seleccion) && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            marginTop: 10,
                            fontSize: 13,
                            color: "#444",
                          }}
                        >
                          {/* flexShrink 0 + tamaño fijo: el texto largo del consentimiento
                              aplastaba el checkbox horizontalmente dentro del flex. */}
                          <IonCheckbox
                            checked={aceptaDescuento}
                            onIonChange={(e) => setAceptaDescuento(e.detail.checked)}
                            style={{
                              flexShrink: 0,
                              width: 24,
                              height: 24,
                              minWidth: 24,
                              marginTop: 2,
                              "--size": "24px",
                            } as any}
                          />
                          <span>
                            Acepto que el total de <strong>{fmtMonto(totalPedido)}</strong>
                            {cuotasSel > 1 ? (
                              <> (en <strong>{cuotasSel} cuotas</strong>)</>
                            ) : null}{" "}
                            se descuente de mi sueldo <strong>únicamente por lo que retire</strong> el día
                            del evento.
                          </span>
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* QR de retiro (confirmado + requiere_qr) */}
              {seleccion.estado_confirmacion === "CONFIRMADO" && Boolean(seleccion.requiere_qr) && (
                <div style={{ marginTop: 16, textAlign: "center" }}>
                  {qrInfo?.ya_retirado ? (
                    <div style={{ background: "#e8f5e9", borderRadius: 8, padding: 12 }}>
                      <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 700 }}>
                        ✓ Ya registraste tu retiro
                      </Typography>
                    </div>
                  ) : qrInfo?.qr ? (
                    <>
                      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        Tu QR de retiro
                      </Typography>
                      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                        Mostralo a la persona encargada el día del evento.
                      </Typography>
                      <div style={{ display: "inline-block", padding: 12, background: "#fff", border: "1px solid #ddd", borderRadius: 12 }}>
                        <QRCodeCanvas value={qrInfo.qr} size={220} level="M" includeMargin={false} />
                      </div>
                      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 6 }}>
                        El código se actualiza en {qrSeconds}s. No compartas capturas.
                      </Typography>
                    </>
                  ) : null}
                </div>
              )}

              {seleccion.estado_confirmacion === "CONFIRMADO" ? (
                <div
                  style={{
                    marginTop: 16,
                    background: "#e8f5e9",
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 700 }}>
                    ✓ Ya confirmaste tu asistencia
                    {(seleccion.acompanantes_confirmados ?? 0) > 0
                      ? ` (con ${seleccion.acompanantes_confirmados} acompañante/s)`
                      : ""}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Te esperamos. Recibirás un recordatorio antes del evento.
                  </Typography>
                  {/* El pedido se puede armar/editar hasta fecha_fin_confirmacion. */}
                  {Boolean(seleccion.requiere_pedido) &&
                    puedeEditarPedido(seleccion) && (
                      <IonButton
                        expand="block"
                        size="small"
                        fill="outline"
                        style={{ marginTop: 8 }}
                        disabled={totalUnidades === 0}
                        onClick={() => confirmar(true)}
                      >
                        {pedidoInfo?.pedido_confirmado ? "Actualizar mi pedido" : "Confirmar mi pedido"}
                      </IonButton>
                    )}
                </div>
              ) : (
                <>
                  {seleccion.max_acompanantes > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
                      <span style={{ fontSize: 14 }}>Acompañantes a llevar:</span>
                      <select
                        value={acomp}
                        onChange={(e) => setAcomp(Number(e.target.value))}
                        style={{ fontSize: 15, padding: "6px 10px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                      >
                        {Array.from({ length: seleccion.max_acompanantes + 1 }, (_, i) => i).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <IonButton expand="block" color="success" style={{ marginTop: 16 }} onClick={() => confirmar(true)}>
                    {Boolean(seleccion.requiere_pedido) && totalUnidades > 0
                      ? `Confirmar pedido (${fmtMonto(totalPedido)})`
                      : "Confirmar asistencia"}
                  </IonButton>
                  <IonButton expand="block" fill="outline" color="medium" onClick={() => confirmar(false)}>
                    No podré asistir
                  </IonButton>
                </>
              )}
            </div>
          ) : null}
        </IonContent>
      </IonModal>
    </div>
  );
};

export default EventosInvitacionCard;
